import { Box } from "@mui/material";
import { useConfirm } from "material-ui-confirm";

import { GetTenantPriceDataQuery } from "~frontend/__generated__/graphql";

import { formatMillicentsAsDollars } from "./priceFormatting";

const mappings = [
  {
    key: "smsSegmentPriceInMillicents",
    label: "SMS Segment",
  },
  {
    key: "mmsMessagePriceInMillicents",
    label: "MMS Message",
  },
  {
    key: "smsUnregisteredSegmentPriceInMillicents",
    label: "SMS Unregistered Segment",
  },
  {
    key: "mmsUnregisteredMessagePriceInMillicents",
    label: "MMS Unregistered",
  },
  {
    key: "smsShortcodeSegmentPriceInMillicents",
    label: "SMS Shortcode Segment",
  },
  {
    key: "mmsShortcodeMessagePriceInMillicents",
    label: "MMS Shortcode",
  },
];

type ConfirmPriceType = (props: {
  newPrices: GetTenantPriceDataQuery["getTenantPriceData"]["defaults"];
  defaults: GetTenantPriceDataQuery["getTenantPriceData"]["defaults"];
}) => Promise<void>;

export const useConfirmPrices = (): ConfirmPriceType => {
  const confirm = useConfirm();

  const confirmPrices = async ({
    newPrices,
    defaults,
  }: {
    newPrices: GetTenantPriceDataQuery["getTenantPriceData"]["defaults"];
    defaults: GetTenantPriceDataQuery["getTenantPriceData"]["defaults"];
  }) => {
    const outsideThreshold = mappings.filter(({ key }) => {
      const value = Number(newPrices[key as keyof typeof newPrices]);
      const defaultValue = Number(defaults[key as keyof typeof defaults]);

      return (
        value &&
        defaultValue &&
        (value >= 3 * defaultValue || value <= 0.33 * defaultValue)
      );
    });

    const unregisteredLower = [
      "smsUnregisteredSegmentPriceInMillicents",
      "mmsUnregisteredMessagePriceInMillicents",
    ].filter(_key => {
      const key = _key as keyof typeof newPrices;
      const unregisteredValue = Number(newPrices[key]);
      const registeredValue = Number(
        newPrices[key.replace("Unregistered", "") as keyof typeof newPrices]
      );

      return (
        unregisteredValue &&
        registeredValue &&
        unregisteredValue < registeredValue
      );
    });

    if (outsideThreshold.length === 0 && unregisteredLower.length === 0) {
      return;
    }

    const { confirmed } = await confirm({
      title: "Confirm Price Changes",
      description: (
        <Box>
          <Box>
            {outsideThreshold.length > 0 && (
              <Box>
                <Box>The following prices are outside the usual threshold:</Box>
                <ul>
                  {outsideThreshold.map(({ key, label }) => {
                    const newPrice = Number(
                      newPrices[key as keyof typeof newPrices]
                    );
                    const defaultPrice = Number(
                      defaults[key as keyof typeof defaults]
                    );
                    return (
                      <li key={`${key}-outside`}>
                        {label}: {formatMillicentsAsDollars(newPrice)} is{" "}
                        {newPrice >= defaultPrice
                          ? "greater than or equal to 3x"
                          : "less than 1/3rd"}{" "}
                        the default price of{" "}
                        {formatMillicentsAsDollars(defaultPrice)}
                      </li>
                    );
                  })}
                </ul>
              </Box>
            )}
            {unregisteredLower.length > 0 && (
              <Box>
                <Box>
                  The following unregistered prices are lower than the
                  registered prices:
                </Box>
                <ul>
                  {unregisteredLower.map(key => (
                    <li key={key}>{key.slice(0, 3).toUpperCase()}</li>
                  ))}
                </ul>
              </Box>
            )}
          </Box>
        </Box>
      ),
      confirmationText: "Confirm",
      cancellationText: "Cancel",
    });
    if (!confirmed) {
      throw new Error("User cancelled");
    }
  };

  return confirmPrices;
};
